import React from 'react';
import PropTypes from 'prop-types';

const Fuse = require('fuse.js');

class DropDown extends React.Component {
  dropdownOpen = false;
  searchText = null;

  // See https://fusejs.io for more options
  searchOptions = {
    keys: ['label'],
    shouldSort: true,
    threshold: 0.3
  };

  constructor(props) {
    super(props);

    this.state = this.resolveState(props);
    this.documentClickHandler = this.documentClickHandler.bind(this);
  }

  resolveState(props) {
    // Shorthand so the next assignment doesn't get silly long
    const sc = props.selectedChoice;

    // Ensure ids passed via props are an array, so we don't have do deal with strings or undefined later on
    const selectedIds = (sc) ? Array.isArray(sc) ? [...sc] : [sc] : [];

    // Cloning the array passed via props, so we can modify it later on and be able to compare objets by ref
    let availableChoices = [...props.choices];

    const selectedChoices = availableChoices.filter(choice => selectedIds.includes(choice.id));
    const inputLabel = this.inputLabelForChoices(selectedChoices);

    availableChoices.forEach(c => c['selected'] = selectedChoices.includes(c) );

    return {
      label: inputLabel,
      selected: selectedChoices,
      choices: availableChoices
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.documentClickHandler);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.documentClickHandler);
  }

  render() {
    return (
      <div
        ref={node => this.node = node}
        className={`dropdown ${(this.dropdownOpen ? 'open' : '')} ${this.props.className}`}>
        <div className="dropdown--input" onClick={this.onInputClick.bind(this)}>
          { this.renderInput() }
          <span className="dropdown--input-arrow" />
        </div>

        { this.dropdownOpen &&
          <div className="dropdown--list">
            { this.renderDropdownItems() }
          </div>
        }
      </div>
    );
  }

  renderInput() {
    if (this.dropdownOpen && this.props.options.searchable) {
      return <input autoFocus className="dropdown--input-search" onChange={this.onSearchInputChange.bind(this)} />;
    }

    return <span className="dropdown--input-label">{ this.state.label }</span>;
  }

  renderDropdownItems() {
    const fuse = new Fuse(this.state.choices, this.searchOptions);
    const choices = (this.searchText) ? fuse.search(this.searchText || '') : this.state.choices;

    return (
      choices.map((choice, key) => {
        const selectedClass = (choice.selected) ? 'selected' : '';

        return (
          <span
            key={key}
            className={`dropdown--list-item ${selectedClass}`}
            onClick={this.onListItemClick.bind(this, choice)}
          >
            { this.props.options.multipleChoice && this.props.options.displayCheckboxes &&
              <span className="dropdown--list-item--checkbox" />
            }
            {choice.label}
          </span>
        );
      })
    );
  }

  documentClickHandler(event) {
    // Click was on this component, do nothing
    if (this.node.contains(event.target)) { return; }

    this.closeDropdown();
  }

  inputLabelForChoices(choices) {
    return choices.map((c) => c.label).join(', ');
  }

  onSearchInputChange(event) {
    this.searchText = (event.target.value) ? event.target.value : null;
    this.forceUpdate();
  }

  onInputClick(event) {
    // We want to close the dropdown on click unless it's searchable and the dropdown is open
    if ((this.props.options.searchable && this.dropdownOpen) &&
      event.target.className !== 'dropdown--input-arrow') {
      return;
    }

    this.toggleDropdown();
  }

  onListItemClick(choice) {
    let selectedChoices = this.state.selected;
    let closeDropdown = false;

    choice.selected = !choice.selected;

    if (this.props.options.multipleChoice) {
      const selectedIndex = selectedChoices.findIndex(sc => sc === choice);

      if (selectedIndex === -1) {
        selectedChoices = [...selectedChoices, choice];
      } else {
        selectedChoices.splice(selectedIndex, 1);
      }
    } else {
      selectedChoices = [choice];
      closeDropdown = true;
    }

    this.setState({
      label: this.inputLabelForChoices(selectedChoices),
      selected: selectedChoices
    }, () => {
      const selectedIds = this.state.selected.map(s => s.id);

      let response;
      switch (selectedIds && selectedIds.length) {
      case 0:
        response = undefined;       // No choices selected, send undefined
        break;
      case 1:
        response = selectedIds[0];  // Single choice selected, send just the id
        break;
      default:
        response = selectedIds;     // Send the array of ids
      }

      if (closeDropdown) { this.closeDropdown(); }

      this.props.onSelectionChange(response);
    });
  }

  openDropdown() {
    this.dropdownOpen = true;
    this.forceUpdate();
  }

  closeDropdown() {
    this.searchText = null;
    this.dropdownOpen = false;
    this.forceUpdate();
  }

  toggleDropdown() {
    if (this.dropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }
}

DropDown.defaultProps = {
  className: '',
  choices: [],
  options: {}
};

DropDown.propTypes = {
  className: PropTypes.string,
  choices: PropTypes.array.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  options: PropTypes.object,
};

export default DropDown;
